/**
 * The agent's own words (spec §2 H): the one editor for them, mounted by the
 * settings page (ModelSection) and by the agent creator, so the two never
 * drift. The words specialize the agent's built-in contract — they cannot
 * replace it or widen what it may read or write. The agent reads this text;
 * its name and description it never sees (owner ruling R8, 2026-10-21).
 *
 * One name for the one thing, wherever it is drawn: "What it should know, in
 * your words". Controlled: the mounting screen owns the value and its save.
 */

import type { ReactNode } from 'react';

export const AGENT_WORDS_LABEL = 'What it should know, in your words';
export const AGENT_WORDS_HINT = 'The agent reads this before it works. It cannot replace the built-in contract or widen what it may read or write.';
export const AGENT_WORDS_PLACEHOLDER = 'For example: We size work against our sprint of two engineers. Treat anything touching billing as at least Medium.';

export function AgentWordsEditor({ id, value, onChange, readOnly, rows = 4, action, contract }: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
  rows?: number;
  /** Save / Remove, when the mounting screen saves on its own. */
  action?: ReactNode;
  /** The built-in contract the words sit under, shown folded. */
  contract?: string;
}) {
  return (
    <div className="space-y-2" data-testid="agent-words-editor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)]" htmlFor={id}>{AGENT_WORDS_LABEL}</label>
          <p className="text-[11px] text-[var(--text-ter)]">{AGENT_WORDS_HINT}</p>
        </div>
        {action}
      </div>
      <textarea
        id={id}
        value={value}
        rows={rows}
        readOnly={readOnly}
        spellCheck={false}
        placeholder={AGENT_WORDS_PLACEHOLDER}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--surface)] focus:ring-1 focus:ring-[var(--mustard)] focus:outline-none leading-relaxed resize-y"
      />
      {contract && (
        <details>
          <summary className="text-[11px] text-[var(--text-ter)] cursor-pointer">The built-in contract it always keeps</summary>
          <p className="text-[11px] text-[var(--text-sec)] mt-1 font-mono whitespace-pre-wrap">{contract}</p>
        </details>
      )}
    </div>
  );
}
